import { css } from '@emotion/react';
import { Add } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import HistoryIcon from '@mui/icons-material/History';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LinkIcon from '@mui/icons-material/Link';
import ThumbDownIconOutlined from '@mui/icons-material/ThumbDownOutlined';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { MUIAvatar } from '../../../components/Avatar/MUIAvatar';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { IListItemDataModel } from '../../../data-models/list-item.data-model';
import { IListDataModel, ListType } from '../../../data-models/list.data-model';
import { useCurrentUser } from '../../../services/hooks/useCurrentUser';
import { useAddListItemToPipeline } from '../../../services/hooks/useListActions';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { companyStateJ } from '../../../services/state/CompanyStateJ';
import { colors } from '../../../theme/colors';
import { formatExternalLink, toURLFriendlyStringWithDashes } from '../../../util/url-utils';
import { LastContacted } from '../../CompanyProfiles2/Header/LastContacted';
import { CompanyDescriptionField } from '../../DealFlow2/components/DealDrawer/DrawerHeader';
import { MenuButton } from '../../Finance2/common-grid-defs/MenuButton';
import { TrackCompanyModal } from '../forms/TrackCompanyModal';
import { activeListStateJ } from '../../../services/state/ListsStateJ';
import { CompanySocialMedia } from '../Summary/components/CompanySocialMedia';
import { useCompanyActions } from '../Summary/hooks/useCompanyActions';
import { getAllItemActionsByPriority, isFollowedByUser } from '../utils/list-utils';
import { CompanyDetails2 } from './CompanyDetails2';
import { ItemCommentsSection } from './ItemCommentsSection';
import { ListItemModals, showListItemAuditDetailsAtom, showListItemDeleteModalAtom } from './ListItemModals';
import { moveListItemFormStateAtom } from './MoveListItemState';
import { TrackedListActions } from './TrackedListActions';
import { UndoButton } from './UndoButton';

const ListItemDetailsWrapper = css`
  display: grid;
  padding: 1.5rem;
  grid-template-rows: min-content min-content 1fr;
  row-gap: 0.5rem;
  overflow: hidden;
`;

const CompanyTitle = css`
  display: grid;
  grid-template-columns: repeat(4, auto);
  align-items: center;
  justify-content: flex-start;
  margin-right: auto;
  gap: 0.5rem;

  & .socials {
    grid-column: span 4;
  }
`;

const MainPanel = css`
  display: grid;
  grid-gap: 1rem;
  overflow: auto;
`;

const ActionsContainer = css`
  display: flex;
  align-items: start;
  gap: 0.3rem;
`;

const disabledIconStyle = { fill: colors.neutral[30] };

export interface IListItemDetailsProps {
  item: IListItemDataModel;
  gotoNext: () => void;
}

export function ListItemDetailsPanel(props: IListItemDetailsProps) {
  const addToPipeline = useAddListItemToPipeline();
  const { handleChangeHideStatus, handleUntrackCompany } = useCompanyActions();
  const companyId = props.item.companyId;
  const company = useAtomValue(companyStateJ(companyId));
  const { item, gotoNext } = props;
  const currentUser = useCurrentUser();
  const isPassed = item.actionType === 'hidden';
  const isTracked = Boolean(company) && isFollowedByUser(company!, currentUser.id);

  const activeList = useAtomValue(activeListStateJ);
  const isTrackingList = activeList?.name === 'Tracked Companies';

  const filteredSocials = useMemo(() => {
    if (!company?.socials) {
      return [];
    }
    return company.socials.filter(
      (s) => s.url.includes('linkedin') || s.url.includes('crunchbase') || s.url.includes('facebook')
    );
  }, [company]);

  const [showTrackModal, setShowTrackModal] = useState(false);
  const onCloseTrackModel = () => setShowTrackModal(false);

  const togglePass = useCallback(() => {
    handleChangeHideStatus({
      action: !isPassed,
      listItem: item,
      userId: currentUser.id,
    });
    if (!isPassed) {
      gotoNext();
    }
  }, [currentUser.id, gotoNext, handleChangeHideStatus, isPassed, item]);

  const doAddToPipeline = useCallback(() => {
    if (!company) {
      return;
    }
    addToPipeline(item, company);
    gotoNext();
  }, [addToPipeline, company, gotoNext, item]);

  const actions = useMemo(() => {
    if (!company) {
      return null;
    }

    if (activeList?.type !== ListType.DISCOVERY) {
      if (isTrackingList) {
        return <TrackedListActions company={company} />;
      } else {
        return <></>;
      }
    }

    const allItemActions = getAllItemActionsByPriority(item, company, currentUser.id);
    const strongestAction = allItemActions[0];
    if (strongestAction === 'pipeline') {
      return (
        <UndoButton
          hasUndo={false}
          disabled
          startIcon={<Add style={isTracked ? disabledIconStyle : undefined} />}
        >
          Added to Pipine
        </UndoButton>
      );
    }
    if (strongestAction === 'hidden') {
      return (
        <UndoButton
          hasUndo={isPassed}
          startIcon={<ThumbDownIconOutlined style={disabledIconStyle} />}
          onClick={togglePass}
        >
          Passed
        </UndoButton>
      );
    }
    if (strongestAction === 'followedByUser') {
      return (
        <UndoButton
          hasUndo
          startIcon={<HourglassEmptyIcon style={disabledIconStyle} />}
          onClick={async () => {
            await handleUntrackCompany(company);
            setShowTrackModal(false);
          }}
        >
          Untrack
        </UndoButton>
      );
    }

    return (
      <>
        <IconButton color='secondary' title='pass' onClick={togglePass}>
          <ThumbDownIconOutlined fontSize='small' />
        </IconButton>

        <IconButton color='secondary' title='track' onClick={() => setShowTrackModal(true)}>
          <HourglassEmptyIcon style={isTracked ? disabledIconStyle : undefined} fontSize='small' />
        </IconButton>

        <IconButton color='secondary' title='add to pipeline' onClick={doAddToPipeline}>
          <AddIcon fontSize='small' />
        </IconButton>
        <ListItemMoreActions sourceList={activeList} company={company} />
      </>
    );
  }, [
    company,
    activeList,
    item,
    currentUser.id,
    togglePass,
    isTracked,
    doAddToPipeline,
    isTrackingList,
    isPassed,
    handleUntrackCompany,
  ]);

  if (!company || !item || !activeList) {
    return null;
  }

  return (
    <>
      <div css={ListItemDetailsWrapper} key={company.id}>
        <TrackCompanyModal
          open={showTrackModal}
          onSubmit={onCloseTrackModel}
          onClose={onCloseTrackModel}
          company={company}
          initialValues={{
            userId: company.assignee ?? undefined,
          }}
        />
        <div css={ActionsContainer}>
          <div css={CompanyTitle}>
            <MUIAvatar src={company.logoUrl} nameParts={company.name.split(/\s+?/)} />
            <Typography
              variant='h6'
              component={Link}
              color='secondary'
              to={`/${ROUTES.COMPANY_PROFILES}/${toURLFriendlyStringWithDashes(`${company?.name}-${companyId}`)}`}
            >
              {company.name}
            </Typography>
            {company.website ? (
              <IconButton href={formatExternalLink(company.website ?? '')} target='_blank'>
                <LinkIcon htmlColor={colors.neutral[60]} fontSize='small' />
              </IconButton>
            ) : null}

            <Stack direction='row' className='socials'>
              <CompanySocialMedia socials={filteredSocials} />
              <Box ml={4}>
                <Suspense fallback={<div />}>
                  <LastContacted companyId={companyId} />
                </Suspense>
              </Box>
            </Stack>
          </div>
          {actions}
        </div>
        <CompanyDescriptionField companyId={company.id} />
        <div css={MainPanel}>
          <CompanyDetails2 company={company} activeList={activeList} />
          <ItemCommentsSection item={item} />
        </div>
      </div>
      <ListItemModals item={item} company={company} />
    </>
  );
}

interface IListItemMoreActionsProps {
  sourceList: IListDataModel;
  company: ICompanyDataModel;
}
function ListItemMoreActions({ sourceList, company }: IListItemMoreActionsProps) {
  const setShowAuditDetails = useSetAtom(showListItemAuditDetailsAtom);
  const setShowDeleteModal = useSetAtom(showListItemDeleteModalAtom);
  const setShowMoveListItemFormState = useSetAtom(moveListItemFormStateAtom);
  const menuItems = useMemo(() => {
    const menuItems = [
      {
        name: 'Show Audit',
        icon: <HistoryIcon />,
        onClick: () => setShowAuditDetails(true),
        disabled: false,
        hide: false,
        divider: false,
        title: 'Show Audit',
      },
    ];
    if (PermissionService.get().hasPermission(PermissionKey.canEditList)) {
      menuItems.push({
        name: 'Move to...',
        icon: <DriveFileMoveOutlinedIcon />,
        onClick: () => setShowMoveListItemFormState({ show: true, company, sourceList }),
        disabled: false,
        hide: false,
        divider: false,
        title: 'Move to...',
      });
      menuItems.push({
        name: 'Delete',
        icon: <DeleteOutlineIcon />,
        onClick: () => setShowDeleteModal(true),
        disabled: false,
        hide: false,
        divider: false,
        title: 'Delete',
      });
    }
    return menuItems;
  }, [company, setShowAuditDetails, setShowDeleteModal, setShowMoveListItemFormState, sourceList]);

  return <MenuButton menuItems={menuItems} />;
}
